/**
 * ComparisonDetail Component
 *
 * Displays comparison details inline in the right column (not as modal).
 * Shows prompt, responses, and actions with a back button to return to list.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PencilIcon, TrophyIcon, QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { logger } from '../../../lib/logging';
import type { Comparison, Response } from '../../../lib/types/results';
import type { ScoringResult } from '../../../services/comparison';
import type { ComparisonDetailView } from '../state/comparisonDetailView';
import type { ConsensusTab } from '../../../features/consensus';
import { RepromptModal } from './RepromptModal';
import { ConsensusContainer } from '../../../features/consensus';
import { ConsensusAnalysisTrigger } from '../../../features/consensus/components/ConsensusAnalysisTrigger';
import { ConsensusGuideModal } from '../consensus/ConsensusGuideModal';
import { ConsensusAnalysisProvider } from '../../../features/consensus/context/ConsensusAnalysisContext';
import { computeWinner } from '../../../utils/winnerComputation';
import { ComparisonResponseCard, type ScoreChipVariant } from './ComparisonResponseCard';
import { deriveScoreRoles, type ScoreRoleMap, type ResponseScoreEntry } from './scoreRoles';
import type { CriteriaScores } from './CriteriaBadges';
import { ResponseDetails } from '../responses/ResponseDetails';
import { logEvent, createTimer } from '../../../utils/telemetry';
import { ResizableSxSPanes } from './ResizableSxSPanes';
import { useIsTruncated } from '../../../hooks/useIsTruncated';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName, normalizeModelIdentifier, buildModelKey, type ModelMetadataMap } from '../../../utils/modelDisplayName';
import { doesResponseMatchRequestedModel } from '../../../utils/modelResolver';
import { BackControlsDual } from '../shared/BackControlsDual';
import { useDevice } from '../../../features/consensus/hooks/useDevice';
import type { DensityOption } from '../state/density';
import { updateComparison, getJudgeModelsForComparison, selectJudgesForScoring, type JudgeModelInfo } from '../../../lib/api/results';
import { SideBySideMatrix } from '../scoring/SideBySideMatrix';
import { ScoringRubricModal } from '../scoring/ScoringRubricModal';
import { ModelBadge } from '../shared/ModelBadge';
import { useMetaTags } from '../../../hooks/useMetaTags';
import { usePageControls } from '../../../contexts/PageControlsContext';
import { ActionButtonsMenu } from './ActionButtonsMenu';
import { TokenQuotaExhaustedModal } from '../../shared/TokenQuotaExhaustedModal';
import { entitlementService } from '../../../services/entitlement';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import { useAuth } from '../../../contexts/AuthContext';
import { auth } from '../../../lib/firebase/config';

const getStatusMeta = (status: Comparison['status'] | undefined) => {
  switch (status) {
    case 'completed':
      return { color: 'bg-green-400', label: 'Status: Completed' } as const;
    case 'running':
      return { color: 'bg-yellow-400', label: 'Status: Processing' } as const;
    case 'queued':
    case 'pending':
      return { color: 'bg-yellow-300', label: 'Status: Pending' } as const;
    case 'failed':
    case 'error':
      return { color: 'bg-red-400', label: 'Status: Failed' } as const;
    case 'canceled':
    case 'cancelled':
      return { color: 'bg-slate-400', label: 'Status: Canceled' } as const;
    default:
      return { color: 'bg-slate-400', label: 'Status: Unknown' } as const;
  }
};

export interface ComparisonDetailProps {
  comparison: Comparison;
  responses: Response[];
  scoringResults?: ScoringResult[];
  compositeScores?: { modelId: string; modelName: string; compositeScore: number; categoryScores: any[] }[];
  scoresEnabled?: boolean;
  onBack: () => void;
  onNavigateToParent?: () => void;
  onNavigateToAll?: () => void;
  onOpenResponse?: (responseId: string) => void;
  onFavoriteToggle?: (comparisonId: string) => void;
  isFavorited?: boolean;
  detailView?: ComparisonDetailView;
  onDetailViewChange?: (view: ComparisonDetailView) => void;
  consensusTab?: ConsensusTab;
  onConsensusTabChange?: (tab: ConsensusTab) => void;
  winnerAiId?: string | null;
  winnerScore?: number | null;
  responsesLoading?: boolean;
  responsesRefreshing?: boolean;
  responseError?: string | null;
  onResponseRetry?: () => void;
  onDeleteComparison?: (comparisonId: string) => void;
  deletingComparisonId?: string | null;
  deleteError?: string | null;
  onDismissDeleteError?: () => void;
  deleteSuccessMessage?: string | null;
  density?: DensityOption;
  onComparisonUpdated?: (comparisonId: string) => void;
  onScoresRefetch?: () => void | Promise<any>;
  // Task 4-5: SxS Compare Deck selection & view mode
  compareDeck?: string[];
  onAddToCompareDeck?: (responseId: string) => void;
  onRemoveFromCompareDeck?: (responseId: string) => void;
  isSxSMode?: boolean; // Task 5: If true, render two-pane layout
  // Task 6: Resizable divider
  sxsSplitPct?: number; // Split percentage (30-70%)
  onSxsSplitChange?: (pct: number) => void;
  // Task 7: Exit SxS mode
  onExitSxS?: () => void;
  // User context
  isAnonymous?: boolean;
}

export const ComparisonDetail: React.FC<ComparisonDetailProps> = ({
  comparison,
  responses,
  scoringResults = [],
  compositeScores,
  scoresEnabled = false,
  onBack,
  onNavigateToParent,
  onNavigateToAll,
  onOpenResponse,
  onFavoriteToggle,
  isFavorited = false,
  detailView = 'outputs',
  onDetailViewChange,
  consensusTab = 'heatmap',
  onConsensusTabChange,
  winnerAiId: propWinnerAiId = null,
  winnerScore: propWinnerScore = null,
  responsesLoading = false,
  responsesRefreshing = false,
  responseError = null,
  onResponseRetry,
  onDeleteComparison,
  deletingComparisonId = null,
  deleteError = null,
  onDismissDeleteError,
  deleteSuccessMessage = null,
  density,
  onComparisonUpdated,
  onScoresRefetch,
  compareDeck = [],
  onAddToCompareDeck,
  onRemoveFromCompareDeck,
  isSxSMode = false,
  sxsSplitPct = 50,
  onSxsSplitChange,
  onExitSxS,
  isAnonymous = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRepromptModalOpen, setIsRepromptModalOpen] = useState(false);
  const [showRubricModal, setShowRubricModal] = useState(false);
  const [showConsensusGuideModal, setShowConsensusGuideModal] = useState(false);
  const [showConsensusTooltip, setShowConsensusTooltip] = useState(false);
  const [showScoresRubricTooltip, setShowScoresRubricTooltip] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<Response | null>(null);
  const isDeletingComparison = deletingComparisonId === comparison.id;
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const { isMobile } = useDevice();
  const showJudgeModules = detailView !== 'consensus';
  const statusMeta = useMemo(() => getStatusMeta(comparison.status), [comparison.status]);
  const pageControls = usePageControls();
  const shareUrl = useMemo(
    () => `${window.location.origin}/results?view=comparisons&id=${comparison.id}`,
    [comparison.id]
  );

  // Set dynamic meta tags for social sharing
  useMetaTags(useMemo(() => {
    const modelNames = comparison.modelsUsed?.slice(0, 3).join(', ') || 'AI models';
    // Use AI-generated title from description field with "Smart Prompt:" prefix
    const title = comparison.description
      ? `Smart Prompt: ${comparison.description}`
      : `Smart Prompt: Compare ${modelNames} responses`;
    // Meta description uses AI-generated description field, falls back to prompt excerpt
    const description = comparison.description
      ? comparison.description
      : `Compare ${modelNames} responses: ${comparison.prompt.slice(0, 100)}${comparison.prompt.length > 100 ? '...' : ''}`;

    return {
      title,
      description,
      url: `${window.location.origin}/results?view=comparisons&id=${comparison.id}`,
      keywords: `AI comparison, ${modelNames}, prompt testing, LLM comparison`,
    };
  }, [comparison.id, comparison.description, comparison.prompt, comparison.modelsUsed]));

  // Manual scoring state
  const [isScoringInProgress, setIsScoringInProgress] = useState(false);
  const [scoringProgress, setScoringProgress] = useState<{current: number, total: number} | null>(null);
  const [scoringError, setScoringError] = useState<string | null>(null);
  const [selectedJudges, setSelectedJudges] = useState<JudgeModelInfo[]>([]);
  const [showTokenQuotaModal, setShowTokenQuotaModal] = useState(false);
  const [tokenQuotaInfo, setTokenQuotaInfo] = useState<{used: number, limit: number, planName: string} | null>(null);
  const [showLogoFlash, setShowLogoFlash] = useState(false);
  const hasHandledLogoFlashRef = useRef(false);
  const multiJudgeEligible = (comparison.totalModels ?? 0) > 1 && responses.length > 0;
  const paywallEnabled = useFeatureFlag('PAYWALL');
  const tokenUsageEnabled = useFeatureFlag('TOKENUSAGE');
  const isBetaEnabled = useFeatureFlag('BETA');
  // NEW_DESIGN_BETA graduated - features always enabled
  const authContext = (() => {
    try {
      return useAuth();
    } catch {
      return null;
    }
  })();

  const userKey = useMemo(
    () => authContext?.dbUserId || authContext?.user?.uid || comparison.userId || 'anonymous',
    [authContext?.dbUserId, authContext?.user?.uid, comparison.userId]
  );
  const nextStepDismissKeys = useMemo(() => ({
    shared: `comparison-detail-next-step-dismissed-${userKey}`,
    responses: `comparison-detail-next-step-dismissed-responses-${userKey}`,
    scores: `comparison-detail-next-step-dismissed-scores-${userKey}`,
  }), [userKey]);
  const [nextStepDismissed, setNextStepDismissed] = useState<{responses: boolean; scores: boolean}>(
    { responses: false, scores: false }
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const sharedValue = window.localStorage.getItem(nextStepDismissKeys.shared);
      const responsesValue = window.localStorage.getItem(nextStepDismissKeys.responses);
      const scoresValue = window.localStorage.getItem(nextStepDismissKeys.scores);

      const sharedDismissed = sharedValue === 'true';
      const responsesDismissed = responsesValue === 'true' || sharedDismissed;
      const scoresDismissed = scoresValue === 'true' || sharedDismissed;

      setNextStepDismissed({ responses: responsesDismissed, scores: scoresDismissed });

      if (sharedDismissed) {
        window.localStorage.setItem(nextStepDismissKeys.responses, 'true');
        window.localStorage.setItem(nextStepDismissKeys.scores, 'true');
        window.localStorage.removeItem(nextStepDismissKeys.shared);
      }
    } catch (error) {
      console.warn('[ComparisonDetail] Unable to read next-step dismissal state', { error });
    }
  }, [nextStepDismissKeys.responses, nextStepDismissKeys.scores, nextStepDismissKeys.shared]);

  // Flash Smart Prompt logo only when redirected from processing
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const locationState = (location.state as { fromProcessing?: boolean } | null) || {};
    const fromProcessingState = locationState.fromProcessing === true;
    const params = new URLSearchParams(location.search);
    const fromProcessingQuery = params.get('from') === 'processing';

    const shouldFlash = !hasHandledLogoFlashRef.current &&
      (fromProcessingState || fromProcessingQuery) &&
      comparison.status === 'completed';

    if (shouldFlash) {
      hasHandledLogoFlashRef.current = true;
      setShowLogoFlash(true);
      const timer = setTimeout(() => setShowLogoFlash(false), 1500);

      if (fromProcessingState) {
        const nextState = { ...locationState, fromProcessing: false };
        navigate(location.pathname + location.search, { replace: true, state: nextState });
      } else if (fromProcessingQuery) {
        params.delete('from');
        const nextSearch = params.toString();
        const nextPath = nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;
        navigate(nextPath, { replace: true, state: location.state });
      }

      return () => clearTimeout(timer);
    }
  }, [comparison.status, location.pathname, location.search, location.state, navigate]);

  const shouldHideResponsesNextStepCta = nextStepDismissed.responses;
  const shouldHideScoresNextStepCta = nextStepDismissed.scores;

  const modelMetadataMap: ModelMetadataMap = useMemo(() => {
    const map: ModelMetadataMap = new Map();

    responses.forEach(response => {
      if (!map.has(response.modelId)) {
        map.set(response.modelId, {
          displayName: response.model?.displayName,
          providerName: response.provider?.name || response.providerName
        });
      }
    });

    return map;
  }, [responses]);

  // Enhanced responses with standardized modelKey for consensus components
  // This ensures consistent matching between claim groups and response objects
  const responsesWithModelKey = useMemo(() => {
    return responses.map(response => ({
      ...response,
      modelKey: buildModelKey(response)
    }));
  }, [responses]);

  const withDisplayNames = useCallback(
    (judges: JudgeModelInfo[]) => judges.map((judge) => {
      const metadata = modelMetadataMap.get(judge.model);
      return {
        ...judge,
        displayName: judge.displayName || metadata?.displayName || formatModelDisplayName(judge.model, modelMetadataMap)
      };
    }),
    [modelMetadataMap]
  );


  // Fetch judge models used for this comparison (for persistent badge display)
  useEffect(() => {
    if (comparison.id && scoresEnabled) {
      getJudgeModelsForComparison(comparison.id).then(judges => {
        if (judges.length > 0) {
          setSelectedJudges(withDisplayNames(judges));
        }
      });
    }
  }, [comparison.id, scoresEnabled, withDisplayNames]);

  // Debug logging for score investigation
  useEffect(() => {
    if (scoresEnabled && compositeScores) {
      console.log('[ComparisonDetail] Scores Debug:', {
        enabled: scoresEnabled,
        count: compositeScores.length,
        scores: compositeScores.map(s => ({
          modelId: s.modelId,
          modelName: s.modelName,
          score: s.compositeScore,
          scoreType: typeof s.compositeScore,
          rawScore: JSON.stringify(s.compositeScore)
        }))
      });
    } else {
      console.log('[ComparisonDetail] No scores:', { scoresEnabled, hasScores: !!compositeScores });
    }
  }, [scoresEnabled, compositeScores]);

  // Inline editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [localDescription, setLocalDescription] = useState<string | null>(null);

  // Display title: Use AI-generated description for clean, human-readable title
  const displayTitle = useMemo(() => {
    // Priority 1: Use local override (optimistic update), then description (AI-generated title from DeepSeek)
    const description = (localDescription || comparison.description)?.trim();
    if (description && description.length > 0 && description !== 'Auto-generated comparison from POC flow') {
      return description; // ✅ AI-generated human-readable title
    }

    // Priority 2: Fallback to title (full original prompt) - truncate for display
    const title = comparison.title?.trim();
    if (title && title.length > 0) {
      return title.length > 100 ? title.substring(0, 100) + '...' : title;
    }

    // Priority 3: Legacy fallback - extract from prompt
    const prompt = comparison.prompt?.trim();
    if (prompt && prompt.length > 0) {
      const webSearchIndex = prompt.indexOf('## Web Search Context');
      if (webSearchIndex > 0) {
        const original = prompt.substring(0, webSearchIndex).trim();
        return original.length > 100 ? original.substring(0, 100) + '...' : original;
      }
      return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
    }

    return 'Untitled comparison';
  }, [localDescription, comparison.description, comparison.title, comparison.prompt]);

  // Full prompt content: Use title for complete original prompt
  const fullPromptContent = useMemo(() => {
    // Always use title for full prompt display
    const title = comparison.title?.trim();
    if (title && title.length > 0) {
      return title; // ✅ Full original user prompt
    }

    // Fallback for old comparisons
    const prompt = comparison.prompt?.trim();
    if (prompt && prompt.length > 0) {
      const webSearchIndex = prompt.indexOf('## Web Search Context');
      if (webSearchIndex > 0) {
        return prompt.substring(0, webSearchIndex).trim();
      }
      return prompt;
    }

    return '';
  }, [comparison.title, comparison.prompt]);

  // Prompt expand/collapse state
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  // Ref for prompt text element to measure if truncated
  const promptTextRef = useRef<HTMLParagraphElement>(null);
  const isTruncated = useIsTruncated(fullPromptContent, 'line-clamp-4', promptTextRef);

  // Always use stacked layout on mobile, respect density prop on desktop
  const effectiveDensity = isMobile ? 'stacked' : (density || 'stacked');
  const allowCompareDeck = responses.length > 1;

  // Set page controls for mobile navigation
  useEffect(() => {
    if (isMobile && !isAnonymous) {
      pageControls.setPageControls({
        show: true, // Show action buttons on all tabs (Responses, Scores, Consensus)
        isFavorited,
        onFavorite: () => onFavoriteToggle?.(comparison.id),
        onDelete: () => {
          if (!onDeleteComparison || isDeletingComparison) return;
          onDismissDeleteError?.();
          setIsDeleteConfirmationVisible(true);
        },
        onReprompt: () => setIsRepromptModalOpen(true),
        onCopy: handleCopyLink,
        // Pass comparison detail view state for mobile header buttons
        comparisonDetailView: detailView,
        onComparisonDetailViewChange: onDetailViewChange,
        comparisonResponsesCount: responses.length,
      });
    } else {
      pageControls.clearPageControls();
    }

    return () => {
      pageControls.clearPageControls();
    };
  }, [
    isMobile,
    isAnonymous,
    detailView,
    isFavorited,
    comparison.id,
    isDeletingComparison,
    onDetailViewChange,
    responses.length,
  ]);

  useEffect(() => {
    if (!isDeletingComparison && !deleteError) {
      setIsDeleteConfirmationVisible(false);
    }
  }, [isDeletingComparison, deleteError]);

  // Reset local description when navigating to different comparison
  useEffect(() => {
    setLocalDescription(null);
  }, [comparison.id]);

  // Title editing handlers
  const handleEditTitleStart = () => {
    setIsEditingTitle(true);
    setEditTitleValue(displayTitle);
    setTitleError(null);
  };

  const handleEditTitleCancel = () => {
    setIsEditingTitle(false);
    setEditTitleValue('');
    setTitleError(null);
  };

  const handleEditTitleSave = async () => {
    const trimmedValue = editTitleValue.trim();

    if (!trimmedValue || trimmedValue.length < 1 || trimmedValue.length > 500) {
      setTitleError('Title must be between 1 and 500 characters');
      return;
    }

    setSavingTitle(true);
    setTitleError(null);

    try {
      await updateComparison(comparison.id, { description: trimmedValue });
      logger.info('comparison_title_saved_detail', undefined, {
        op: 'comparison_title_save',
        component: 'ComparisonDetail',
        comparisonId: comparison.id,
        success: true
      });

      // Optimistic update - set local state immediately
      setLocalDescription(trimmedValue);

      setIsEditingTitle(false);
      setEditTitleValue('');

      // Trigger parent refetch
      if (onComparisonUpdated) {
        onComparisonUpdated(comparison.id);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update title';
      setTitleError(message);
      logger.error('comparison_title_save_error_detail', undefined, {
        op: 'comparison_title_save',
        component: 'ComparisonDetail',
        comparisonId: comparison.id,
        success: false,
        err_message: message
      });
    } finally {
      setSavingTitle(false);
    }
  };

  // Pass 6 (Fixed): Navigation handlers using callbacks from parent
  const handleNavigateToParent = useCallback(() => {
    logger.info('BackControlsDual: Navigating to parent', {
      comparisonId: comparison.id,
      target: 'parent',
      hasParent: !!comparison.parentId,
      parentId: comparison.parentId
    });

    if (onNavigateToParent) {
      onNavigateToParent();
    }
  }, [comparison.id, comparison.parentId, onNavigateToParent]);

  const handleNavigateToAll = useCallback(() => {
    logger.info('BackControlsDual: Navigating to all comparisons', {
      comparisonId: comparison.id,
      target: 'all'
    });

    if (onNavigateToAll) {
      onNavigateToAll();
    }
  }, [comparison.id, onNavigateToAll]);

  // Compute winner dynamically from scoring results for this comparison
  // This ensures each comparison shows its own winner, not a global winner
  const { winnerId: computedWinnerId, winnerScore: computedWinnerScore } = useMemo(() => {
    return computeWinner(responses, scoringResults, compositeScores);
  }, [responses, scoringResults, compositeScores]);

  // Use computed winner (fallback to prop for backward compatibility with API)
  const winnerAiId = computedWinnerId ?? propWinnerAiId;
  const winnerScore = computedWinnerScore ?? propWinnerScore;

  const handleRepromptSubmit = (comparisonId: string, prompt: string, selectedModels: string[]) => {
    logger.info('reprompt_comparison_created', undefined, {
      op: 'reprompt_comparison_created',
      component: 'ComparisonDetail',
      originalComparisonId: comparison.id,
      newComparisonId: comparisonId,
      promptLength: prompt.length,
      modelCount: selectedModels.length,
      models: selectedModels
    });

    // Navigate to processing page
    navigate(`/compare/${comparisonId}/processing`, {
      state: {
        comparisonId,
        prompt,
        models: selectedModels
      }
    });
  };

  // Handle response card click - open response detail view
  const handleResponseClick = (response: Response, isWinner: boolean) => {
    const timer = createTimer();

    logEvent({
      op: 'response_card_clicked',
      component: 'ComparisonDetail',
      success: true,
      response_id: response.id,
      comparison_id: comparison.id,
      is_winner: isWinner,
      duration_ms: timer.end(),
    });

    // Navigate to Responses view with fullscreen detail
    if (onOpenResponse) {
      onOpenResponse(response.id);
    }
  };

  const resolveResponseScores = useCallback((response: Response): Record<string, number> | undefined => {
    if (response.scores && Object.keys(response.scores).length > 0) {
      return response.scores as Record<string, number>;
    }

    if (!compositeScores || compositeScores.length === 0) {
      return undefined;
    }

    const compositeScore = compositeScores.find(cs => cs.modelId === response.modelId);
    if (compositeScore?.criterionScores && Array.isArray(compositeScore.criterionScores)) {
      const fallbackScores: Record<string, number> = {};

      compositeScore.criterionScores.forEach((criterion: any) => {
        if (typeof criterion?.criterionName !== 'string') {
          return;
        }

        if (typeof criterion?.score !== 'number' || Number.isNaN(criterion.score)) {
          return;
        }

        fallbackScores[criterion.criterionName] = criterion.score;
      });

      if (Object.keys(fallbackScores).length > 0) {
        return fallbackScores;
      }
    }

    return undefined;
  }, [compositeScores]);

  // Handle back from response detail - return to comparison outputs
  const handleBackFromResponse = () => {
    setSelectedResponse(null);

    logEvent({
      op: 'response_detail_back',
      component: 'ComparisonDetail',
      success: true,
      comparison_id: comparison.id,
    });
  };

  const handleCopyLink = async () => {
    const timer = createTimer();

    try {
      await navigator.clipboard.writeText(shareUrl);

      logEvent({
        op: 'copy_link_clicked',
        component: 'ComparisonDetail',
        success: true,
        comparison_id: comparison.id,
        duration_ms: timer.end(),
      });
    } catch (error) {
      logEvent({
        op: 'copy_link_clicked',
        component: 'ComparisonDetail',
        success: false,
        comparison_id: comparison.id,
        err_message: error instanceof Error ? error.message : 'Copy failed',
        duration_ms: timer.end(),
      });
    }
  };

  const handleNextStepDismiss = (surface: 'responses' | 'scores') => {
    let success = true;
    let errMessage: string | undefined;

    setNextStepDismissed(prev => ({ ...prev, [surface]: true }));

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(nextStepDismissKeys[surface], 'true');
      } catch (error) {
        success = false;
        errMessage = error instanceof Error ? error.message : 'localStorage_unavailable';
        console.warn('[ComparisonDetail] Unable to persist next-step dismissal state', { error });
      }
    }

    logEvent({
      op: 'next_step_dismiss',
      component: 'ComparisonDetail',
      comparison_id: comparison.id,
      surface,
      success,
      flag: 'NEW_DESIGN_BETA',
      err_message: errMessage,
    });
  };

  // Log analytics event when winner is viewed (fires once on mount if winner exists)
  React.useEffect(() => {
    if (winnerAiId && winnerScore !== null) {
      logger.info('comparison_winner_viewed', undefined, {
        op: 'comparison_winner_viewed',
        comparison_id: comparison.id,
        winner_ai_id: winnerAiId,
        winner_score: winnerScore,
        source_page: 'detail',
        correlation_id: crypto.randomUUID()
      });
    }
  }, [comparison.id, winnerAiId, winnerScore]);

  // Log when winner is highlighted in Responses view (fires once per view switch)
  React.useEffect(() => {
    if (detailView === 'outputs' && winnerAiId) {
      logger.info('outputs_winner_highlight_viewed', undefined, {
        op: 'outputs_winner_highlight_viewed',
        comparison_id: comparison.id,
        winner_ai_id: winnerAiId
      });
    }
  }, [detailView, comparison.id, winnerAiId]);

  // Calculate winner (highest average score) for sorting, but use API winner for badge
  // Use compositeScores from LLM judge, fallback to legacy scoring
  const responseScores = useMemo<ResponseScoreEntry[]>(() => {
    return responses.map(response => {
      let avgScore = 0;

      // Priority 1: Use compositeScores from LLM judge hook
      if (scoresEnabled && compositeScores && compositeScores.length > 0) {
        const compositeScore = compositeScores.find(cs => cs.modelId === response.modelId);
        if (compositeScore) {
          avgScore = compositeScore.compositeScore;
        }
      }
      // Priority 2: Use legacy scoringResults
      else {
        const scores = scoringResults
          .filter(s => s.responseId === response.id)
          .map(s => s.score);

        if (scores.length > 0) {
          avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
        } else if (response.scoreOverall != null) {
          // Priority 3: Fallback to response-level scoreOverall
          avgScore = response.scoreOverall;
        }
      }

      return { response, avgScore };
    });
  }, [responses, scoringResults, compositeScores, scoresEnabled]);

  const responseScoreRoles = useMemo<ScoreRoleMap>(() => {
    return deriveScoreRoles(responseScores);
  }, [responseScores]);

  const resolveScoreChipVariant = useCallback(
    (role: ScoreRoleMap[string] = 'nonwinner'): ScoreChipVariant => {
      return role === 'winner' ? 'winner-trophy-solid' : 'nonwinner-neutral';
    },
    []
  );

  // Sort by score descending and ensure API winner (if present) is surfaced first
  const sortedResponses = useMemo(() => {
    const sorted = [...responseScores].sort((a, b) => b.avgScore - a.avgScore);

    if (!winnerAiId) {
      return sorted;
    }

    const winnerIndex = sorted.findIndex(({ response }) => response.modelId === winnerAiId);
    if (winnerIndex <= 0) {
      return sorted;
    }

    const [winnerEntry] = sorted.splice(winnerIndex, 1);
    return [winnerEntry, ...sorted];
  }, [responseScores, winnerAiId]);

  // Helper to check if a model ID matches ANY response
  // Uses modelResolver for robust, future-proof matching
  const hasMatchingResponse = (requestedModelId: string, responses: Response[]): boolean => {
    return responses.some(response => doesResponseMatchRequestedModel(requestedModelId, response));
  };

  // Find models without responses (requested but not completed)
  const modelsWithoutResponses = useMemo(() => {
    if (!comparison.modelsUsed || comparison.modelsUsed.length === 0) {
      return [];
    }

    return comparison.modelsUsed.filter(modelId => !hasMatchingResponse(modelId, responses));
  }, [comparison.modelsUsed, responses]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="h-full flex flex-col">
      {/* Logo flash animation */}
      {showLogoFlash && (
        <div className="fixed top-16 inset-x-0 bottom-0 z-[10000] flex items-center justify-center pointer-events-none">
          <img
            src="/logos/Smart Prompt Logo.png"
            alt="Smart Prompt"
            className="w-48 h-48 object-contain animate-[fadeInOut_1.5s_ease-in-out]"
          />
        </div>
      )}
      {deleteSuccessMessage && (
        <div
          role="status"
          className="mx-6 mt-4 mb-2 rounded-md border border-sky-500/40 bg-slate-900/70 px-4 py-3 text-sm text-slate-100 shadow-lg shadow-slate-900/30"
        >
          {deleteSuccessMessage}
        </div>
      )}
      {deleteError && !isDeleteConfirmationVisible && (
        <div
          role="alert"
          className="mx-6 mt-4 mb-2 rounded-md border border-rose-500/50 bg-slate-900/70 px-4 py-3 text-sm text-rose-100 flex items-start justify-between gap-3 shadow-lg shadow-rose-900/30"
        >
          <span>{deleteError}</span>
          {onDismissDeleteError && (
            <button
              onClick={onDismissDeleteError}
              className="text-xs text-red-200/80 hover:text-red-100"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteConfirmationVisible && onDeleteComparison && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="bg-slate-950/95 border border-slate-700/60 rounded-lg shadow-xl p-4 max-w-sm w-full"
          >
            <h4 className="text-sm font-semibold text-white mb-2">Confirm delete</h4>
            <p className="text-xs text-slate-400 mb-3">
              This permanently removes the comparison and its cached responses.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (isDeletingComparison) {
                    return;
                  }
                  onDeleteComparison(comparison.id);
                }}
                disabled={isDeletingComparison}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-sm font-medium text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isDeletingComparison ? (
                  <svg
                    className="w-4 h-4 animate-spin text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 12a8 8 0 018-8"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m-3 0h14" />
                  </svg>
                )}
                <span>{isDeletingComparison ? 'Deleting…' : 'Delete'}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsDeleteConfirmationVisible(false);
                  onDismissDeleteError?.();
                }}
                disabled={isDeletingComparison}
                className="px-3 py-1.5 rounded-md border border-slate-600 text-sm font-medium text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
            {deleteError && (
              <p className="mt-3 text-xs text-red-300" role="alert">
                {deleteError}
              </p>
            )}
          </div>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-slate-900/70 backdrop-blur-sm border-b border-slate-700/50">
          <div className="relative px-6 py-4 flex items-center gap-3 h-[72px]">
            {/* Pass 7: Final dual back controls (Comparisons show only All button) */}
            {!isMobile && (
              <BackControlsDual
                parentTitle={displayTitle}
                onNavigateToParent={handleNavigateToParent}
                onNavigateToAll={handleNavigateToAll}
                showParentButton={false}
              />
            )}

            {/* Comparison Title - Desktop and Mobile */}
            {displayTitle && displayTitle !== 'Untitled comparison' && (
              <div className={`flex-1 flex items-center justify-start ${!isMobile ? 'px-4' : 'px-2 min-w-0'}`}>
                {isEditingTitle ? (
                  <div className="w-full">
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="text"
                        value={editTitleValue}
                        onChange={(e) => setEditTitleValue(e.target.value)}
                        className="flex-1 px-3 py-2 bg-slate-900 border border-cyan-500/50 rounded-lg text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-cyan-400 min-w-0"
                        maxLength={500}
                        autoFocus
                      />
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={handleEditTitleSave}
                          disabled={savingTitle}
                          className="p-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-md disabled:opacity-50 opacity-80 hover:opacity-100 transition-opacity flex items-center justify-center"
                          aria-label={savingTitle ? 'Saving...' : 'Save changes'}
                          title="Save"
                        >
                          {savingTitle ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={handleEditTitleCancel}
                          disabled={savingTitle}
                          className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-md disabled:opacity-50 opacity-80 hover:opacity-100 transition-opacity flex items-center justify-center"
                          aria-label="Cancel editing"
                          title="Cancel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {titleError && (
                      <p className="text-xs text-red-400 mt-1">{titleError}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h2
                      className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-white ${isMobile ? '' : 'select-text cursor-text'} text-left truncate flex-1 min-w-0`}
                      {...(!isMobile && { onDoubleClick: handleEditTitleStart })}
                      {...(!isMobile && { title: 'Double-click to edit' })}
                    >
                      {displayTitle}
                    </h2>
                    {isMobile && (
                      <button
                        onClick={handleEditTitleStart}
                        className="flex-shrink-0 p-1.5 text-slate-400 hover:text-slate-200 transition-colors min-w-[32px]"
                        aria-label="Edit title"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {!isAnonymous &&
                !(isMobile && (detailView === 'outputs' || detailView === 'scoring' || detailView === 'consensus')) && (
                  <ActionButtonsMenu
                    isOpen={isActionMenuOpen}
                    onToggle={() => setIsActionMenuOpen(!isActionMenuOpen)}
                    onClose={() => setIsActionMenuOpen(false)}
                    isDeletingComparison={isDeletingComparison}
                    isFavorited={isFavorited}
                    onFavorite={() => onFavoriteToggle?.(comparison.id)}
                    onCopy={handleCopyLink}
                    onReprompt={() => setIsRepromptModalOpen(true)}
                    onDelete={() => {
                      if (isDeletingComparison) {
                        return;
                      }
                      onDismissDeleteError?.();
                      setIsDeleteConfirmationVisible(true);
                    }}
                  />
                )}
            </div>
          </div>
        </header>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 py-5">
          <div className="flex flex-col gap-6 mb-6">
            {/* Prompt Section - Only show on Responses tab */}
            {detailView === 'outputs' && (
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/50 border-l-4 border-l-cyan-500" data-testid="prompt-card">
                <div className={isBetaEnabled ? "mb-4" : "mb-3"}>
                  <h3 className={isBetaEnabled ? "text-lg font-bold text-slate-300 uppercase tracking-wider" : "text-sm font-semibold text-slate-400 uppercase tracking-wider"}>
                    YOUR PROMPT
                  </h3>
                </div>
                {fullPromptContent ? (
                  <>
                    <p
                      ref={promptTextRef}
                      className={`text-slate-200 text-sm whitespace-pre-wrap ${!isPromptExpanded ? 'line-clamp-4' : ''}`}
                    >
                      {fullPromptContent}
                    </p>
                    {isTruncated && (
                      <div className="mt-2 flex w-full items-center gap-3 justify-start">
                        <button
                          onClick={() => {
                            const newExpandedState = !isPromptExpanded;
                            setIsPromptExpanded(newExpandedState);
                            logger.info(newExpandedState ? 'prompt_expanded' : 'prompt_collapsed', undefined, {
                              op: newExpandedState ? 'prompt_expanded' : 'prompt_collapsed',
                              component: 'ComparisonDetail',
                              comparisonId: comparison.id
                            });
                          }}
                          className="shrink-0 text-sm text-cyan-300 hover:text-cyan-200 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-1"
                          aria-expanded={isPromptExpanded}
                          aria-label={isPromptExpanded ? 'Show less of the prompt' : 'Show more of the prompt'}
                        >
                          {isPromptExpanded ? 'Show less' : 'Show more'}
                        </button>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 mt-3">
                      <span>{formatDate(comparison.createdAt)}</span>
                      <div className="flex items-center gap-1.5 text-slate-300">
                        <span
                          aria-hidden="true"
                          className={`inline-block w-2 h-2 rounded-full ${statusMeta.color}`}
                        />
                        <span className="sr-only">{statusMeta.label}</span>
                        <span className="capitalize text-slate-400">
                          {comparison.status ?? 'unknown'}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-400 text-sm">No prompt provided.</p>
                )}
              </div>
            )}

          </div>

          {/* LLM Judge Scoring Leaderboard - HIDDEN: Composite scores now shown on Response cards */}
          {/* {showJudgeModules && (() => {
            // Only render leaderboard if there are valid scores (> 0)
            const shouldRender = scoresEnabled &&
              Array.isArray(compositeScores) &&
              compositeScores.length > 0 &&
              compositeScores.some(s => s.compositeScore > 0);
            console.log('[ComparisonDetail] Pre-render check:', {
              scoresEnabled,
              isArray: Array.isArray(compositeScores),
              length: compositeScores?.length,
              shouldRender,
              compositeScores
            });

            return shouldRender ? (
              <div className="px-6 py-4 border-t border-slate-700/50">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  LLM Judge Scores
                </h3>
                <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {compositeScores
                      .filter(s => s.compositeScore != null && s.compositeScore > 0)
                      .sort((a, b) => (b.compositeScore ?? 0) - (a.compositeScore ?? 0))
                      .map((scoreData, index) => (
                        <div
                          key={scoreData.modelId}
                          className="p-2 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                              index === 0 ? 'bg-yellow-500 text-slate-900' :
                              index === 1 ? 'bg-slate-400 text-slate-900' :
                              index === 2 ? 'bg-orange-600 text-white' : 'bg-slate-600 text-slate-300'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="w-5 h-5 bg-slate-700/30 rounded flex items-center justify-center p-0.5 flex-shrink-0">
                              <img
                                src={getModelLogo(scoreData.modelId, scoreData.modelName)}
                                alt={`${scoreData.modelName} logo`}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <span className="font-medium text-slate-200 text-sm truncate">
                              {formatModelDisplayName(scoreData.modelId)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 ml-12">
                            <span className="text-base font-bold text-white min-w-[2.5rem]">
                              {(scoreData.compositeScore ?? 0).toFixed(2)}
                            </span>
                            <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all ${
                                  index === 0 ? 'bg-yellow-500' :
                                  index === 1 ? 'bg-slate-400' :
                                  index === 2 ? 'bg-orange-600' : 'bg-cyan-500'
                                }`}
                                style={{ width: `${((scoreData.compositeScore ?? 0) / 10) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="mt-2 pt-2 border-t border-slate-700/50 text-xs text-slate-500">
                    Scored by GPT-4o-mini (OpenAI) • {compositeScores.length} models evaluated
                  </div>
                </div>
              </div>
            ) : null;
          })()} */}

          {/* Task 5-6-7: Side-by-Side View with Resizable Divider and Exit (when isSxSMode = true) */}
          {isSxSMode && detailView === 'outputs' && compareDeck.length === 2 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                  Side-by-Side Comparison
                </h3>
                {/* Task 7: Exit side-by-side button */}
                {onExitSxS && (
                  <button
                    onClick={() => {
                      logger.info('sxs_exit_clicked', comparison.correlationId, {
                        op: 'sxs_exit',
                        component: 'ComparisonDetail',
                        correlationId: comparison.correlationId,
                        comparisonId: comparison.id,
                        success: true
                      });
                      onExitSxS();
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-300 hover:text-white bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/50 hover:border-slate-500/70 rounded-lg transition-all"
                    aria-label="Exit side-by-side view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Exit Side-by-Side View
                  </button>
                )}
              </div>

              {/* Task 6: Resizable two-pane layout (30-70% bounds, keyboard support) */}
              <div className="h-[calc(100vh-300px)] min-h-[600px]">
                {(() => {
                  // Calculate highest score for SxS mode
                  const leftResponse = responses.find(r => r.id === compareDeck[0]);
                  const rightResponse = responses.find(r => r.id === compareDeck[1]);

                  const leftScore = leftResponse ? (
                    compositeScores?.find(cs => cs.modelId === leftResponse.modelId)?.compositeScore ??
                    responseScores.find(rs => rs.response.id === compareDeck[0])?.avgScore ?? 0
                  ) : 0;

                  const rightScore = rightResponse ? (
                    compositeScores?.find(cs => cs.modelId === rightResponse.modelId)?.compositeScore ??
                    responseScores.find(rs => rs.response.id === compareDeck[1])?.avgScore ?? 0
                  ) : 0;

                  const highestScore = Math.max(leftScore, rightScore);

                  return (
                    <ResizableSxSPanes
                      initialSplitPct={sxsSplitPct}
                      onSplitChange={onSxsSplitChange}
                      correlationId={comparison.correlationId}
                      leftPane={(() => {
                        const response = responses.find(r => r.id === compareDeck[0]);
                        if (!response) return null;

                        const { avgScore } = responseScores.find(rs => rs.response.id === compareDeck[0]) || { avgScore: 0 };
                        const isWinner = winnerAiId && response.modelId === winnerAiId;

                        // Use composite score from LLM judge if available, otherwise fall back to avgScore
                        const compositeScore = compositeScores?.find(cs => cs.modelId === response.modelId);
                        const displayScore = compositeScore?.compositeScore ?? avgScore;
                        const isHighestScore = displayScore > 0 && displayScore === highestScore;

                        // Extract criteria scores from criterionScores array
                        const criteriaScores: Partial<CriteriaScores> = {};
                        if (compositeScore?.criterionScores && Array.isArray(compositeScore.criterionScores)) {
                          compositeScore.criterionScores.forEach((criterion: any) => {
                            if (criterion.criterionName && criterion.score !== undefined) {
                              criteriaScores[criterion.criterionName as keyof CriteriaScores] = criterion.score;
                            }
                          });
                        }

                        return (
                          <div className="pr-2">
                            <ComparisonResponseCard
                              response={response}
                              isWinner={isWinner}
                              isHighestScore={isHighestScore}
                              score={displayScore}
                              onClick={() => handleResponseClick(response, isWinner)}
                              density={effectiveDensity}
                              isInCompareDeck={true}
                              onRemoveFromCompare={onRemoveFromCompareDeck}
                              canAddToCompare={false}
                              isSxSMode={true}
                              criteriaScores={criteriaScores}
                              scoreChipVariant={resolveScoreChipVariant(responseScoreRoles[response.id])}
                              scoreRole={responseScoreRoles[response.id] ?? 'nonwinner'}
                              comparisonId={comparison.id}
                              comparisonCorrelationId={comparison.correlationId}
                            />
                          </div>
                        );
                      })()}
                      rightPane={(() => {
                        const response = responses.find(r => r.id === compareDeck[1]);
                        if (!response) return null;

                        const { avgScore } = responseScores.find(rs => rs.response.id === compareDeck[1]) || { avgScore: 0 };
                        const isWinner = winnerAiId && response.modelId === winnerAiId;

                        // Use composite score from LLM judge if available, otherwise fall back to avgScore
                        const compositeScore = compositeScores?.find(cs => cs.modelId === response.modelId);
                        const displayScore = compositeScore?.compositeScore ?? avgScore;
                        const isHighestScore = displayScore > 0 && displayScore === highestScore;

                        // Extract criteria scores from criterionScores array
                        const criteriaScores: Partial<CriteriaScores> = {};
                        if (compositeScore?.criterionScores && Array.isArray(compositeScore.criterionScores)) {
                          compositeScore.criterionScores.forEach((criterion: any) => {
                            if (criterion.criterionName && criterion.score !== undefined) {
                              criteriaScores[criterion.criterionName as keyof CriteriaScores] = criterion.score;
                            }
                          });
                        }

                        return (
                          <div className="pl-2">
                            <ComparisonResponseCard
                              response={response}
                              isWinner={isWinner}
                              isHighestScore={isHighestScore}
                              score={displayScore}
                              onClick={() => handleResponseClick(response, isWinner)}
                              density={effectiveDensity}
                              isInCompareDeck={true}
                              onRemoveFromCompare={onRemoveFromCompareDeck}
                              canAddToCompare={false}
                              isSxSMode={true}
                              criteriaScores={criteriaScores}
                              scoreChipVariant={resolveScoreChipVariant(responseScoreRoles[response.id])}
                              scoreRole={responseScoreRoles[response.id] ?? 'nonwinner'}
                              comparisonId={comparison.id}
                              comparisonCorrelationId={comparison.correlationId}
                            />
                          </div>
                        );
                      })()}
                    />
                  );
                })()}
              </div>
            </div>
          )}

          {/* Responses View */}
          {!isSxSMode && detailView === 'outputs' && (
            <div>
              {/* Visual separator - V2 flag */}
              {isBetaEnabled && (
                <div className="mb-6 border-t border-slate-700/50" aria-hidden="true" />
              )}

              {!selectedResponse ? (
                <>
                  <div className={isBetaEnabled ? "mb-5 space-y-3" : "mb-4 space-y-3"}>
                    <div className="flex items-center justify-between">
                      <h3 className={isBetaEnabled ? "text-lg font-bold text-slate-300 uppercase tracking-wider" : "text-sm font-semibold text-slate-400 uppercase tracking-wider"}>
                        Model Responses
                      </h3>
                      <div className="flex items-center space-x-3 text-xs text-slate-500">
                        {responsesRefreshing && (
                          <span
                            className="flex items-center space-x-1 text-cyan-300"
                            role="status"
                            aria-live="polite"
                          >
                            <svg
                              className="w-3 h-3 animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 12a8 8 0 018-8" />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M20 12a8 8 0 01-8 8 8 8 0 01-7.446-4.684"
                                opacity={0.6}
                              />
                            </svg>
                            <span>Refreshing</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Instruction text */}
                    <p className="text-xs text-slate-400">
                      Select up to two responses to compare them side by side.
                    </p>

                    {/* Next Step CTA */}
                    {!shouldHideResponsesNextStepCta && (
                      <div className="p-3 bg-slate-800/30 border border-cyan-500/40 rounded-lg">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs text-slate-300">
                            <span className="font-semibold">Next step:</span> Run Multi-Judge Scoring to grade these responses on 7 criteria.{' '}
                            <button
                              onClick={() => onDetailViewChange?.('scoring')}
                              className="text-cyan-400 hover:text-cyan-300 underline transition-colors inline-flex items-center gap-1"
                            >
                              Go to Scores &gt;
                            </button>
                          </p>

                          <button
                            type="button"
                            aria-label="Dismiss next step"
                            onClick={() => handleNextStepDismiss('responses')}
                            className="text-slate-400 hover:text-slate-200 transition-colors"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {responseError && responses.length > 0 && (
                    <div
                      className="mb-3 p-3 border border-amber-500/40 bg-amber-500/10 text-amber-100 text-xs rounded-lg"
                      role="status"
                      aria-live="polite"
                    >
                      Unable to refresh responses: {responseError}. Showing cached data.
                    </div>
                  )}

                  <div
                    className={`
                ${effectiveDensity === 'stacked' ? 'space-y-4' :
                  effectiveDensity === 'comfortable' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' :
                  'grid grid-cols-1 md:grid-cols-3 gap-3'}
              `}
                    aria-live="polite"
                  >
                    {responsesLoading && responses.length === 0 ? (
                      [0, 1].map(index => (
                        <div
                          key={`response-skeleton-${index}`}
                          className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 animate-pulse"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-slate-700/40 rounded-lg" />
                              <div>
                                <div className="h-3 w-24 bg-slate-700/40 rounded" />
                                <div className="h-3 w-16 bg-slate-700/30 rounded mt-2" />
                              </div>
                            </div>
                            <div className="h-3 w-10 bg-slate-700/40 rounded" />
                          </div>
                          <div className="space-y-2">
                            <div className="h-3 w-full bg-slate-700/30 rounded" />
                            <div className="h-3 w-5/6 bg-slate-700/30 rounded" />
                            <div className="h-3 w-2/3 bg-slate-700/30 rounded" />
                          </div>
                        </div>
                      ))
                    ) : responseError && responses.length === 0 ? (
                      <div className="bg-red-900/20 border border-red-700/40 rounded-xl p-6 text-center">
                        <h4 className="text-sm font-semibold text-red-200 mb-2">Failed to load responses</h4>
                        <p className="text-red-300 text-xs mb-4">{responseError}</p>
                        {onResponseRetry && (
                          <button
                            onClick={onResponseRetry}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 transition-colors"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    ) : !responsesLoading && sortedResponses.length === 0 ? (
                      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 text-center">
                        <h4 className="text-sm font-semibold text-white mb-2">No responses available yet</h4>
                        <p className="text-slate-400 text-xs mb-4">
                          Responses will appear here once the models finish generating output.
                        </p>
                        {onResponseRetry && (
                          <button
                            onClick={onResponseRetry}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/30 transition-colors"
                          >
                            Refresh
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {(() => {
                          // Calculate highest score across all responses
                          const highestScore = Math.max(
                            ...sortedResponses.map(({ response, avgScore }) => {
                              const compositeScore = compositeScores?.find(cs => cs.modelId === response.modelId);
                              return compositeScore?.compositeScore ?? avgScore;
                            }),
                            0
                          );

                          return sortedResponses.map(({ response, avgScore }) => {
                            const isWinner = winnerAiId && response.modelId === winnerAiId;

                            // Use composite score from LLM judge if available, otherwise fall back to avgScore
                            const compositeScore = compositeScores?.find(cs => cs.modelId === response.modelId);
                            const displayScore = compositeScore?.compositeScore ?? avgScore;

                            // Check if this is the highest score
                            const isHighestScore = displayScore > 0 && displayScore === highestScore;

                            // Extract criteria scores from criterionScores array
                            const criteriaScores: Partial<CriteriaScores> = {};
                            if (compositeScore?.criterionScores && Array.isArray(compositeScore.criterionScores)) {
                              compositeScore.criterionScores.forEach((criterion: any) => {
                                if (criterion.criterionName && criterion.score !== undefined) {
                                  criteriaScores[criterion.criterionName as keyof CriteriaScores] = criterion.score;
                                }
                              });
                            }

                            return (
                              <ComparisonResponseCard
                                key={response.id}
                                response={response}
                                isWinner={isWinner}
                                isHighestScore={isHighestScore}
                                score={displayScore}
                                onClick={() => handleResponseClick(response, isWinner)}
                                density={effectiveDensity}
                                isInCompareDeck={allowCompareDeck && compareDeck.includes(response.id)}
                                onAddToCompare={allowCompareDeck ? onAddToCompareDeck : undefined}
                                onRemoveFromCompare={allowCompareDeck ? onRemoveFromCompareDeck : undefined}
                                canAddToCompare={allowCompareDeck && compareDeck.length < 2}
                                criteriaScores={criteriaScores}
                                scoreChipVariant={resolveScoreChipVariant(responseScoreRoles[response.id])}
                                scoreRole={responseScoreRoles[response.id] ?? 'nonwinner'}
                                comparisonId={comparison.id}
                                comparisonCorrelationId={comparison.correlationId}
                              />
                            );
                          });
                        })()}

                        {/* Placeholder cards for models without responses */}
                        {modelsWithoutResponses.map(modelId => {
                          const displayName = formatModelDisplayName(modelId);
                          const providerName = modelId.includes('deepseek') ? 'DeepSeek' :
                                               modelId.includes('claude') ? 'Anthropic' :
                                               modelId.includes('gpt') ? 'OpenAI' :
                                               modelId.includes('gemini') ? 'Google' : 'Unknown';

                          return (
                            <div
                              key={modelId}
                              className="bg-slate-800/20 border border-slate-700/30 rounded-xl p-5 opacity-60"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  {/* Model Logo */}
                                  <div className="w-8 h-8 bg-slate-700/30 rounded-lg flex items-center justify-center p-1">
                                    <img
                                      src={getModelLogo(modelId, providerName)}
                                      alt={`${displayName} logo`}
                                      className="w-full h-full object-contain opacity-50"
                                    />
                                  </div>

                                  {/* Model Name */}
                                  <div>
                                    <h4 className="text-sm font-semibold text-slate-400">
                                      {displayName}
                                    </h4>
                                  </div>
                                </div>

                                {/* Status Badge */}
                                <div className="flex items-center space-x-1 px-2 py-1 rounded-md bg-slate-700/30 border border-slate-600/30">
                                  <div className="w-2 h-2 rounded-full bg-yellow-500/60 animate-pulse" />
                                  <span className="text-xs text-slate-400">Pending</span>
                                </div>
                              </div>

                              {/* Placeholder Content */}
                              <p className="text-xs text-slate-500 mb-3">
                                Response is being generated or failed to complete. This model was requested but hasn't returned a response yet.
                              </p>

                              {/* Footer */}
                              <div className="flex items-center space-x-3 text-xs text-slate-600">
                                <span>Awaiting response...</span>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* AI-generated disclaimer at bottom */}
                    <div className="mt-4 text-[11px] text-slate-500">
                      AI-generated · May contain errors.
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Scoring View */}
          {detailView === 'scoring' && (
            <div>
              {/* Scores Comparison Matrix Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                    Scores Comparison Matrix
                  </h3>
                  {/* Tooltip icon with rubric link */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowScoresRubricTooltip(!showScoresRubricTooltip)}
                      onBlur={() => setTimeout(() => setShowScoresRubricTooltip(false), 200)}
                      className="text-slate-400 hover:text-cyan-300 transition-colors"
                      aria-label="Learn about our scoring system"
                    >
                      <QuestionMarkCircleIcon className="w-4 h-4" />
                    </button>
                    {showScoresRubricTooltip && (
                      <div className="absolute z-50 left-1/2 -translate-x-1/2 top-7 w-64 max-w-[calc(100vw-32px)] sm:max-w-xs bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-3">
                        <p className="text-slate-300 text-xs mb-2">
                          Learn about our scoring system{' '}
                          <button
                            type="button"
                            onClick={() => {
                              setShowScoresRubricTooltip(false);
                              setShowRubricModal(true);
                            }}
                            className="text-cyan-400 hover:text-cyan-300 transition-colors underline"
                          >
                            View Full Rubric
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-slate-400 text-xs space-y-3 mb-4">
                  <p>
                    {(() => {
                      // Check if scores exist and are populated
                      const hasValidScores = scoresEnabled &&
                        compositeScores &&
                        compositeScores.length > 0 &&
                        compositeScores.some(s => s.compositeScore > 0);

                      if (hasValidScores) {
                        return 'Click any row to view the full response.';
                      } else {
                        return 'Score responses to see detailed analysis and rankings from multiple AI judges.';
                      }
                    })()}
                  </p>

                  {/* Next Step CTA - Show when scores exist and there are multiple responses to compare */}
                  {(() => {
                    const hasValidScores = scoresEnabled &&
                      compositeScores &&
                      compositeScores.length > 0 &&
                      compositeScores.some(s => s.compositeScore > 0) &&
                      responses.length > 1;

                    if (hasValidScores && !shouldHideScoresNextStepCta) {
                      return (
                        <div className="p-3 bg-slate-800/30 border border-cyan-500/40 rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs text-slate-300">
                              <span className="font-semibold">Next Step:</span> See which models align (and where they differ). Go to{' '}
                              <button
                                onClick={() => onDetailViewChange?.('consensus')}
                                className="text-cyan-400 hover:text-cyan-300 underline transition-colors inline-flex items-center gap-1"
                              >
                                Consensus
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </p>

                            <button
                              type="button"
                              aria-label="Dismiss next step"
                              onClick={() => handleNextStepDismiss('scores')}
                              className="text-slate-400 hover:text-slate-200 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Judge models - persist after scoring completes */}
                {selectedJudges.length > 0 && !isScoringInProgress && (
                  <div className="mb-4 bg-slate-900/40 border border-slate-700/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-2">
                      {selectedJudges.length > 1 ? 'Judged by these models:' : 'Judged by this model:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedJudges.map((judge, index) => (
                        <ModelBadge
                          key={`${judge.provider}-${judge.model}-persistent-${index}`}
                          provider={judge.provider}
                          model={judge.model}
                          displayName={judge.displayName}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline Scoring Controls - Show only if no valid scores exist and not currently scoring */}
                {(() => {
                  const hasValidScores = scoresEnabled &&
                    compositeScores &&
                    compositeScores.length > 0 &&
                    compositeScores.some(s => s.compositeScore > 0);

                  if (hasValidScores || isScoringInProgress) {
                    return null;
                  }

                  return (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={async () => {
                          // Check token quota before scoring (gated behind TOKENUSAGE flag)
                          console.log('[ScoreResponses] Token quota check starting...', {
                            tokenUsageEnabled,
                            timestamp: new Date().toISOString()
                          });

                          if (tokenUsageEnabled) {
                            try {
                              // First, verify we're authenticated
                              const user = auth.currentUser;
                              if (!user) {
                                console.error('[ScoreResponses] ❌ No authenticated user found');
                                setScoringError('Please sign in to use this feature.');
                                return;
                              }

                              console.log('[ScoreResponses] Fetching entitlement status...', {
                                userEmail: user.email,
                                userId: user.uid
                              });

                              const status = await entitlementService.getStatus();

                              console.log('[ScoreResponses] Entitlement status received:', {
                                paywallEnabled: status.paywallEnabled,
                                plan: status.plan,
                                hasTokenQuota: !!status.quotas?.tokens,
                                tokenQuota: status.quotas?.tokens,
                              });

                              // CRITICAL FIX: Check if token quota is missing from response
                              // This prevents bypass when backend errors cause missing quota data
                              if (!status.quotas?.tokens) {
                                console.error('[ScoreResponses] ❌ Token quota missing from response', {
                                  hasQuotas: !!status.quotas,
                                  quotasKeys: status.quotas ? Object.keys(status.quotas) : [],
                                  isLoggedIn: !!user,
                                  userEmail: user?.email,
                                  paywallEnabled: status.paywallEnabled,
                                  planCode: status.plan?.code,
                                  timestamp: new Date().toISOString()
                                });
                                // Show quota modal with fail-closed values to block the action
                                // This handles the case where server couldn't retrieve token quota
                                setTokenQuotaInfo({
                                  used: 0,
                                  limit: 0,
                                  planName: status.plan?.displayName || 'Unknown'
                                });
                                setShowTokenQuotaModal(true);
                                return; // Block scoring
                              }

                              // Check if token quota is exceeded
                              if (!status.quotas.tokens.allowed) {
                                console.log('[ScoreResponses] ❌ TOKEN QUOTA EXCEEDED - BLOCKING', {
                                  used: status.quotas.tokens.used,
                                  limit: status.quotas.tokens.limit,
                                  allowed: status.quotas.tokens.allowed,
                                  percentage: status.quotas.tokens.percentage
                                });
                                setTokenQuotaInfo({
                                  used: status.quotas.tokens.used,
                                  limit: status.quotas.tokens.limit,
                                  planName: status.plan.displayName
                                });
                                setShowTokenQuotaModal(true);
                                return; // Block scoring
                              }

                              console.log('[ScoreResponses] ✅ Token quota check passed, proceeding with scoring', {
                                used: status.quotas.tokens.used,
                                limit: status.quotas.tokens.limit,
                                remaining: status.quotas.tokens.remaining
                              });
                            } catch (error) {
                              console.error('[ScoreResponses] CRITICAL ERROR: Failed to check token quota - BLOCKING:', error);
                              // CRITICAL FIX: Fail CLOSED - block scoring on errors
                              setScoringError('Unable to verify token quota. Please try again.');
                              return; // Block scoring
                            }
                          } else {
                            console.log('[ScoreResponses] Paywall disabled, skipping token check');
                          }

                          setIsScoringInProgress(true);
                          setScoringError(null);
                          setScoringProgress({ current: 0, total: responses.length });

                          // Multi-judge scoring is always enabled
                          const shouldUseMultiJudge = true;

                          // If using multi-judge scoring, fetch judges immediately for instant display
                          if (shouldUseMultiJudge) {
                            try {
                              const judges = await selectJudgesForScoring(comparison.id);
                              if (judges.length > 0) {
                                setSelectedJudges(withDisplayNames(judges));
                              }
                            } catch (error) {
                              console.error('Failed to select judges:', error);
                            }
                          }

                          // Simulate progress updates during scoring
                          let progressInterval: NodeJS.Timeout | null = setInterval(() => {
                            setScoringProgress(prev => {
                              if (!prev) return null;
                              const newCurrent = Math.min(prev.current + 1, prev.total);
                              return { current: newCurrent, total: prev.total };
                            });
                          }, 15000);

                          try {
                            const response = await fetch(`/api/results/${comparison.id}/score`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                judgeProvider: 'deepseek',
                                judgeModel: 'deepseek-chat',
                                useMultiJudge: shouldUseMultiJudge,
                                forceRescore: false
                              })
                            });

                            if (progressInterval) clearInterval(progressInterval);

                            if (!response.ok) {
                              throw new Error(`Scoring failed: HTTP ${response.status}`);
                            }

                            const result = await response.json();

                            if (result.judgeMetrics?.judges) {
                              setSelectedJudges(withDisplayNames(result.judgeMetrics.judges));
                            }

                            setScoringProgress({ current: responses.length, total: responses.length });
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            if (onScoresRefetch) {
                              const refetchResult = onScoresRefetch();
                              if (refetchResult instanceof Promise) {
                                await refetchResult;
                              }
                            }

                            if (onComparisonUpdated) {
                              onComparisonUpdated(comparison.id);
                            }

                            setIsScoringInProgress(false);
                            setScoringProgress(null);

                          } catch (error) {
                            if (progressInterval) clearInterval(progressInterval);
                            console.error('[ManualScoring] Error:', error);
                            setScoringError(error instanceof Error ? error.message : 'Scoring failed');
                            setIsScoringInProgress(false);
                            setScoringProgress(null);
                            setSelectedJudges([]);
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg transition-colors font-medium text-sm w-fit"
                      >
                        <TrophyIcon className="w-4 h-4" />
                        <span>Score Responses</span>
                      </button>


                      {/* Next Step CTA - Moved below Multi-Judge Scoring */}
                      {!shouldHideScoresNextStepCta && responses.length > 1 && (
                        <div className="p-3 bg-slate-800/30 border border-cyan-500/40 rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs text-slate-300">
                              <span className="font-semibold">Next Step:</span> See which models align (and where they differ). Go to{' '}
                              <button
                                onClick={() => onDetailViewChange?.('consensus')}
                                className="text-cyan-400 hover:text-cyan-300 underline transition-colors inline-flex items-center gap-1"
                              >
                                Consensus
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </p>

                            <button
                              type="button"
                              aria-label="Dismiss next step"
                              onClick={() => handleNextStepDismiss('scores')}
                              className="text-slate-400 hover:text-slate-200 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {scoringError && (
                        <div className="p-2 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-200">
                          {scoringError}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Inline Scoring Progress - Show only when scoring is in progress */}
              {isScoringInProgress && (
                <div className="mb-6 bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-6 h-6 animate-spin text-cyan-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 12a8 8 0 018-8"
                        />
                      </svg>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-white">
                          {scoringProgress?.current === scoringProgress?.total
                            ? 'Loading Scores...'
                            : 'Scoring Responses...'}
                        </p>
                        {scoringProgress && scoringProgress.current < scoringProgress.total && (
                          <p className="text-xs text-slate-400 mt-1">
                            Evaluating {scoringProgress.current} of {scoringProgress.total} responses
                          </p>
                        )}
                        {scoringProgress?.current === scoringProgress?.total && (
                          <p className="text-xs text-slate-400 mt-1">
                            Fetching scores from database...
                          </p>
                        )}
                      </div>
                    </div>
                    {scoringProgress && (
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(scoringProgress.current / scoringProgress.total) * 100}%`
                          }}
                        />
                      </div>
                    )}

                    {/* Judge Model Badges */}
                    {selectedJudges.length > 0 && (
                      <div className="border-t border-slate-700/50 pt-3">
                        <p className="text-xs text-slate-400 mb-2">
                          {selectedJudges.length > 1 ? 'Judges for this scoring run:' : 'Judge for this scoring run:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedJudges.map((judge, index) => (
                            <ModelBadge
                              key={`${judge.provider}-${judge.model}-${index}`}
                              provider={judge.provider}
                              model={judge.model}
                              displayName={judge.displayName}
                              size="small"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <SideBySideMatrix
                scores={compositeScores || []}
                responses={responses}
                onOpenResponse={(responseId) => {
                  const response = responses.find(r => r.id === responseId);
                  if (response) {
                    const isWinner = winnerAiId && response.modelId === winnerAiId;
                    handleResponseClick(response, isWinner);
                  }
                }}
                showHeader={false}
              />

              <p className="text-[11px] text-slate-500 mt-6">
                AI-generated · May contain errors.
              </p>
            </div>
          )}

          {/* Consensus View - Feature Flagged (FF_RESULTS_CONSENSUS) */}
          {detailView === 'consensus' && (
            <ConsensusAnalysisProvider comparisonId={comparison.id}>
              <div>
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                      Consensus Analysis
                    </h3>
                  {/* Tooltip icon with guide link */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowConsensusTooltip(!showConsensusTooltip)}
                      onBlur={() => setTimeout(() => setShowConsensusTooltip(false), 200)}
                      className="text-slate-400 hover:text-cyan-300 transition-colors"
                      aria-label="Learn about consensus analysis"
                    >
                      <QuestionMarkCircleIcon className="w-4 h-4" />
                    </button>
                    {showConsensusTooltip && (
                      <div className="absolute z-50 left-1/2 -translate-x-1/2 top-7 w-64 max-w-[calc(100vw-32px)] sm:max-w-xs bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-3">
                        <p className="text-slate-300 text-xs mb-2">
                          Learn how consensus analysis works{' '}
                          <button
                            type="button"
                            onClick={() => {
                              setShowConsensusTooltip(false);
                              setShowConsensusGuideModal(true);
                            }}
                            className="text-cyan-400 hover:text-cyan-300 transition-colors underline"
                          >
                            View Full Guide
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-slate-400 text-xs mb-4">
                  Review claims extracted from model responses and see which models agree on key points.
                </p>

                {/* Inline Consensus Analysis Trigger - Only show if not completed */}
                <ConsensusAnalysisTrigger
                  comparisonId={comparison.id}
                  onAnalysisComplete={() => {
                    // Refresh or update state if needed
                  }}
                />
              </div>
                <ConsensusContainer
                  comparison={comparison}
                  responses={responsesWithModelKey}
                  scoringResults={scoringResults}
                  correlationId={crypto.randomUUID()}
                  activeTab={consensusTab}
                  onTabChange={onConsensusTabChange}
                />
              </div>
            </ConsensusAnalysisProvider>
          )}
        </div>
      </div>

      {/* Reprompt Modal */}
      <RepromptModal
        isOpen={isRepromptModalOpen}
        onClose={() => setIsRepromptModalOpen(false)}
        initialPrompt={fullPromptContent}
        initialSelectedModels={comparison.modelsUsed}
        onSubmit={handleRepromptSubmit}
      />

      {/* Scoring Rubric Modal */}
      <ScoringRubricModal
        isOpen={showRubricModal}
        onClose={() => setShowRubricModal(false)}
      />

      {/* Consensus Guide Modal */}
      <ConsensusGuideModal
        isOpen={showConsensusGuideModal}
        onClose={() => setShowConsensusGuideModal(false)}
      />

      {/* Token Quota Exhausted Modal - Blocking for scoring/consensus */}
      {showTokenQuotaModal && tokenQuotaInfo && (
        <TokenQuotaExhaustedModal
          isOpen={showTokenQuotaModal}
          onClose={() => setShowTokenQuotaModal(false)}
          usedTokens={tokenQuotaInfo.used}
          totalTokens={tokenQuotaInfo.limit}
          planName={tokenQuotaInfo.planName}
          blocking={false}
        />
      )}
    </div>
  );
};
